/**
 * The block resize grip (spec §8.6 item 8) `[M21]`.
 *
 * One grip for every block type, mounted by `BlockChrome` on the inside of the block box's lower
 * edge — generalised from the webpage block's own handle, which it replaces. A drag previews the new
 * edge and commits once on pointer-up, so it is one Loro commit and one undo step rather than one
 * per pixel. Arrow keys resize from the keyboard; double-click, Enter or Space resets the block to
 * its natural height.
 */
import { useRef, useState } from "react";
import { clsx } from "clsx";
import { MAX_BLOCK_HEIGHT, MIN_BLOCK_HEIGHT, clampHeight } from "./height";
import { useT } from "../i18n";

const STEP = 16;
const BIG_STEP = 64;

export interface ResizeGripProps {
  /** the block's current height, or null when it has never been resized */
  height: number | null;
  /** the height the grip starts from when there is none yet: the block's rendered height */
  naturalHeight: () => number;
  onResize(px: number): void;
  onReset(): void;
}

export function ResizeGrip({ height, naturalHeight, onResize, onReset }: ResizeGripProps) {
  const t = useT();
  const start = useRef<{ y: number; h: number } | null>(null);
  const [delta, setDelta] = useState(0);
  const from = () => height ?? clampHeight(naturalHeight());

  const step = (by: number) => onResize(from() + by);

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label={t("block.resize")}
      aria-valuenow={height ?? undefined}
      aria-valuemin={MIN_BLOCK_HEIGHT}
      aria-valuemax={MAX_BLOCK_HEIGHT}
      tabIndex={0}
      title={t("block.resize.title")}
      data-testid="block-resize"
      data-resized={height ? "true" : undefined}
      className={clsx(
        "pointer-events-auto h-1.5 w-16 cursor-ns-resize rounded-full bg-neutral-300 opacity-0 transition hover:bg-neutral-400 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent group-hover:opacity-100 dark:bg-neutral-700 dark:hover:bg-neutral-600",
        delta && "opacity-100",
      )}
      style={{ transform: delta ? `translateY(${delta}px)` : undefined }}
      onPointerDown={(e) => {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        start.current = { y: e.clientY, h: from() };
        e.preventDefault();
      }}
      onPointerMove={(e) => start.current && setDelta(e.clientY - start.current.y)}
      onPointerUp={(e) => {
        if (start.current) {
          const moved = e.clientY - start.current.y;
          if (moved) onResize(start.current.h + moved);
        }
        start.current = null;
        setDelta(0);
      }}
      onDoubleClick={onReset}
      onKeyDown={(e) => {
        const big = e.shiftKey ? BIG_STEP : STEP;
        if (e.key === "ArrowDown") step(big);
        else if (e.key === "ArrowUp") step(-big);
        else if (e.key === "Enter" || e.key === " ") onReset();
        else return;
        e.preventDefault();
      }}
    />
  );
}
